/* Theme, as three explicit choices.

   Exists because removing the header on mobile removed the theme toggle's only
   home (ADR 0007 decision 3). It is not a mobile-only control though — one
   implementation, both breakpoints (principle 3), so the header's toggle and
   this stay in sync through core/theme.

   Segmented rather than the header's cycle button: a cycle is fine for a
   one-glyph affordance you press repeatedly, but a settings row should show all
   the options and which one is active. 'Auto' is a real third state, not the
   absence of a choice, so it is a button like the others.

   Gapless since ADR 0011: three `g-button full` in a flex row measured 92.7 x
   40.5 with 3-4px between them — under the 44px floor, and a gap too small to
   aim at. A bar of equal segments has no gap to miss into at all.

   The "currently light/dark" caption is rendered by g-settings, not here: prose
   belongs to the group, controls belong to the component. */

import { LitElement, html, css } from 'lit';
import { shadowReset, splitBar } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import type { ThemePref } from '../../core/types.js';
import '../../component/g-icon/g-icon.js';

const OPTIONS: { value: ThemePref; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

@customElement('settings-appearance')
export class SettingsAppearance extends LitElement {
  static override styles = [
    shadowReset,
    splitBar,
    css`
      :host {
        display: block;
      }
      /* splitBar is two halves; this one is thirds. Everything else about a
         segment — the 44px floor, centering, the divider, the press state — is
         the shared fragment's. */
      .seg {
        grid-template-columns: repeat(3, 1fr);
      }
      .act[aria-pressed='true'] {
        background: color-mix(in oklab, var(--accent) 16%, transparent);
        color: var(--accent);
        font-weight: 700;
      }
      /* The tint is the selected state; a hover that repaints it would read as
         deselection under the pointer. */
      @media (hover: hover) {
        .act[aria-pressed='true']:hover {
          background: color-mix(in oklab, var(--accent) 16%, transparent);
        }
      }
    `,
  ];

  /** Owned by the shell so the header's toggle and this never disagree. */
  @property() pref: ThemePref = 'auto';

  private pick(value: ThemePref) {
    this.dispatchEvent(
      new CustomEvent<ThemePref>('theme-pref', {
        detail: value,
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    return html`
      <div class="split seg" role="group" aria-label="Theme">
        ${OPTIONS.map(
          (o) => html`
            <button
              class="act"
              aria-pressed=${this.pref === o.value}
              @click=${() => this.pick(o.value)}
            >
              <g-icon name=${`theme-${o.value}`}></g-icon><span>${o.label}</span>
            </button>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settings-appearance': SettingsAppearance;
  }
}
